import { storeAllEntities } from '@/students/store/helpers'
import * as api from '@/lti/api'
import toast from '@/services/toast'

const getDefaultSubscribableState = () => {
  return {
    subscribables: {},
    subscribable_config: {},
    subscribableGroupings: {},
    subscribableGroupingDetails: {},
    subscriptions: {},
    mySubscriptions: {},
    event_clusters: {},
    events: {},
    courses: {},
    studentCourseContexts: {},
    groups: {},
    students: {},
    lecturers: {},
    classrooms: {},
    student_connections: {},
    activeActivities: {},
    activeSubscribables: {},
    subscribable_statistics: {},
    subscribable_counts: {},
    myAdvises: {},
    questionTemplates: {},
    questionTemplateAnswers: {},
    studentSubscribableAnswers: {},
    myAnswers: {}, // only used for studyplan
    myQueueSubscriptions: {},
    myStudentSubscribableGroupings: {},
    labelGroups: {},
    labels: {},
  }
}

export default {
  state: getDefaultSubscribableState,

  mutations: {
    ADD_MY_SUBSCRIPTION_TO_SUBSCRIBABLE (state, { subscribableId, subscriptionId }) {
      const subscribable = state.subscribables[subscribableId]

      if (subscribable != null) {
        // We set MySubscription
        subscribable.mySubscription = subscriptionId
      }
    },
    ADD_ADVISE_TO_SUBSCRIBABLE (state, { subscribableId, adviseId }) {
      const subscribable = state.subscribables[subscribableId]

      if (subscribable != null) {
        // We set MyAdvises
        subscribable.myAdvises.push(adviseId)
      }
    },
    ADD_MY_ANSWER_TO_SUBSCRIBABLE (state, { subscribableId, studentAnswerId }) {
      const subscribable = state.subscribables[subscribableId]

      if (subscribable != null && !subscribable.myAnswers.includes(studentAnswerId)) {
        // We set MyAnswers
        subscribable.myAnswers.push(studentAnswerId)
      }
    },
    ADD_MY_QUEUE_SUBSCRIPTION_TO_SUBSCRIBABLE (state, { subscribableId, queueSubscriptionId }) {
      const subscribable = state.subscribables[subscribableId]

      if (subscribable != null) {
        subscribable.myQueueSubscription = queueSubscriptionId
      }
    },
    CLEAR_SUBSCRIBABLES_STORE (state) {
      state.subscribables = {}
      state.subscriptions = {}
      state.mySubscriptions = {}
      state.myQueueSubscriptions = {}
      state.event_clusters = {}
      state.events = {}
      state.courses = {}
      state.studentCourseContexts = {}
      state.groups = {}
      state.myAdvises = {}
      state.students = {}
      state.lecturers = {}
      state.classrooms = {}
      state.student_connections = {}
      state.subscribable_statistics = {}
      state.subscribable_counts = {}
      state.questionTemplates = {}
      state.questionTemplateAnswers = {}
      state.studentSubscribableAnswers = {}
      state.myStudentSubscribableGroupings = {}
    },
    CLEAR_NON_SUBSCRIPTIONS (state) {
      const mySubscribableIds = Object.values(state.mySubscriptions).map(mySubscription => mySubscription.subscribableId)

      Object.values(state.subscribables).forEach(subscribable => {
        if (!mySubscribableIds.includes(subscribable.id)) {
          Vue.delete(state.subscribables, subscribable.id)
        }
      })
    },
    CLEAR_SUBSCRIBABLES (state) {
      // We also want to remove the events associated to the subscribable
      // here unless there is a my subscription for calendar
      Object.values(state.subscribables).forEach((subscribable) => {
        if (subscribable.mySubscription == null) {
          Object.values(subscribable.events).forEach(id => {
            delete state.events[id]
          })
        }
      })

      state.subscribables = {}
    },
    CLEAR_MY_SUBSCRIPTION (state, { subscriptionId }) {
      const subscribable = Object.values(state.subscribables).filter(subscribable => {
        return subscribable.mySubscription != null && subscribable.mySubscription == subscriptionId
      })[0]

      // Remove my subscription here
      if (subscribable) {
        subscribable.mySubscription = null
      }

      Vue.delete(state.mySubscriptions, subscriptionId)
    },
    CLEAR_MY_QUEUE_SUBSCRIPTION (state, { subscribableId }) {
      const subscribable = state.subscribables[subscribableId]

      const queueSubscriptionId = subscribable.myQueueSubscription

      if (queueSubscriptionId != null) {
        subscribable.myQueueSubscription = null

        Vue.delete(state.myQueueSubscriptions, queueSubscriptionId)
      }
    },
    RESET_SUBSCRIBABLE_STATE (state) {
      Object.assign(state, getDefaultSubscribableState())
    },

    UPDATE_SUBSCRIPTIONS_COUNT (state, { subscribableId, subscriptionsCount, placesAvailable }) {
      const subscribable = state.subscribables[subscribableId]
      if (subscribable != null) {
        subscribable.subscriptionsCount = subscriptionsCount
        subscribable.placesAvailable = placesAvailable
      }
    },

    TOGGLE_SUBSCRIBABLE_COLLAPSE (state, { subscribableId }) {
      if (state.activeSubscribables[subscribableId] != null) {
        Vue.delete(state.activeSubscribables, subscribableId)
      } else {
        Vue.set(state.activeSubscribables, subscribableId, subscribableId)
      }
    },

    TOGGLE_EVENT_IDS (state, { eventIds, subscribableId }) {
      eventIds.forEach(eventId => {
        if (Object.values(state.activeActivities).some(id => id === eventId)) {
          if (!Object.values(state.activeSubscribables).some(id => {
            if (subscribableId === id) {
              return false
            }

            return state.subscribables[id].events.includes(eventId)
          })) {
            Vue.delete(state.activeActivities, eventId)
          }
        } else {
          Vue.set(state.activeActivities, eventId, eventId)
        }
      })
    },
    REMOVE_SUBSCRIPTION_FROM_GROUPING (state, { subscribableGroupingId, mySubscriptionId }) {
      const subscribableGrouping = state.subscribableGroupings[subscribableGroupingId]

      // if the grouping exists, remove the subscribable from it
      if (subscribableGrouping != null) {
        subscribableGrouping.mySubscriptions = subscribableGrouping.mySubscriptions.filter(mySub => mySub !== mySubscriptionId)

        const subscribableGroupingDetails = state.subscribableGroupingDetails[subscribableGrouping.subscribableGroupingDetails]

        // if the details exist, lower the count by 1
        if (subscribableGroupingDetails) {
          subscribableGroupingDetails.mySubscriptionCount--
        }
      }
    },

    ADD_SUBSCRIPTION_TO_GROUPING (state, { subscribableGroupingId, mySubscriptionId }) {
      const subscribableGrouping = state.subscribableGroupings[subscribableGroupingId]

      // if the grouping exists, remove the subscribable from it
      if (subscribableGrouping != null) {
        subscribableGrouping.mySubscriptions.push(mySubscriptionId[0])

        const subscribableGroupingDetails = state.subscribableGroupingDetails[subscribableGrouping.subscribableGroupingDetails]

        // if the details exist, lower the count by 1
        if (subscribableGroupingDetails) {
          subscribableGroupingDetails.mySubscriptionCount++
        }
      }
    },

    SET_MY_STUDENT_SUBSCRIBABLE_GROUPING_TO_GROUPING (state, { subscribableGroupingId, myStudentSubscribableGroupingId }) {
      const subscribableGrouping = state.subscribableGroupings[subscribableGroupingId]

      if (subscribableGrouping != null) {
        subscribableGrouping.myStudentSubscribableGrouping = myStudentSubscribableGroupingId
      }
    },
  },
  actions: {
    getSubscribables ({ commit, state }, { queryParams }) {
      return api.getSubscribables(queryParams)
        .then(storeAllEntities(commit))
    },
    getSubscribable ({ commit, state }, { subscribableId, queryParams }) {
      return api.getSubscribable(subscribableId, queryParams)
        .then(storeAllEntities(commit))
    },
    getSubscribableGroupings ({ commit, state }, { queryParams }) {
      return api.getSubscribableGroupings(queryParams)
        .then(storeAllEntities(commit))
    },
    getSubscribableGroupingDetails ({ commit, state }, { subscribableGrouping, queryParams }) {
      return api.getSubscribableGroupingDetails(subscribableGrouping, queryParams)
        .then(storeAllEntities(commit))
    },
    validateSubscribableGrouping ({ commit, state }, { subscribableGroupingId, queryParams }) {
      return api.validateSubscribableGrouping(subscribableGroupingId, queryParams)
        .then(resources => {
          return resources
        })
    },
    confirmSubscribableGrouping ({ commit, state }, { subscribableGroupingId, queryParams }) {
      return api.confirmSubscribableGrouping(subscribableGroupingId, queryParams)
        .then(storeAllEntities(commit))
        .then(resources => {
          commit('SET_MY_STUDENT_SUBSCRIBABLE_GROUPING_TO_GROUPING', {
            subscribableGroupingId,
            myStudentSubscribableGroupingId: resources.primaryResources.map(s => s.id)[0],
          })

          return resources
        })
    },
    getSubscriptionHistory ({ commit, state }, { queryParams }) {
      return api.getSubscriptionHistory(queryParams)
        .then(storeAllEntities(commit))
    },
    clearSubscribablesStore ({ commit }) {
      commit('CLEAR_SUBSCRIBABLES_STORE')
    },
    clearNonSubscriptions ({ commit }) {
      commit('CLEAR_NON_SUBSCRIPTIONS')
    },
    clearSubscribables ({ commit }) {
      commit('CLEAR_SUBSCRIBABLES')
    },
    storeSubscription ({ commit, state }, { subscribableId, data }) {
      return api.storeSubscription(subscribableId, data)
        .then(storeAllEntities(commit))
        .then(resources => {
          commit('ADD_MY_SUBSCRIPTION_TO_SUBSCRIBABLE', {
            subscribableId,
            subscriptionId: resources.primaryResources.map(s => s.id),
          })
          if (data.subscribableGroupingId) {
            commit('ADD_SUBSCRIPTION_TO_GROUPING', {
              subscribableGroupingId: data.subscribableGroupingId,
              mySubscriptionId: resources.primaryResources.map(s => s.id),
            })
          }
        })
    },
    deleteSubscription ({ commit, state }, { subscriptionId, data }) {
      return api.deleteSubscription(subscriptionId)
        .then(() => {
          commit('CLEAR_MY_SUBSCRIPTION', { subscriptionId })

          if (data.subscribableGroupingId) {
            commit('REMOVE_SUBSCRIPTION_FROM_GROUPING', {
              subscribableGroupingId: data.subscribableGroupingId,
              mySubscriptionId: subscriptionId,
            })
          }
        })
    },
    updateSubscription ({ commit, state }, { subscriptionId, data }) {
      return api.updateSubscription(subscriptionId, data)
    },
    resetSubscribableState ({ commit, state }) {
      commit('RESET_SUBSCRIBABLE_STATE')
    },

    subscriptionsCountUpdate ({ commit, state }, { subscribableId, subscriptionsCount, placesAvailable }) {
      commit('UPDATE_SUBSCRIPTIONS_COUNT', { subscribableId, subscriptionsCount, placesAvailable })
    },

    getEvents ({ commit, state }, { queryParams }) {
      return api.getEvents(queryParams).then(storeAllEntities(commit))
    },

    getFilterOptions ({ commit, state }, { queryParams }) {
      return api.getFilterOptions(queryParams)
    },

    toggleEvents ({ commit, state }, { eventIds, subscribableId }) {
      commit('TOGGLE_EVENT_IDS', { eventIds: Object.values(eventIds), subscribableId })
    },

    toggleSubscribable ({ commit, state }, { subscribableId }) {
      commit('TOGGLE_SUBSCRIBABLE_COLLAPSE', { subscribableId })
    },

    getSubscribableStatistics ({ commit, state }) {
      return api.getSubscribableStatistics()
        .then(storeAllEntities(commit))
    },

    getSubscribableConfig ({ commit, state }) {
      return api.getSubscribableConfig()
        .then(storeAllEntities(commit))
    },

    getSubscribableCount ({ commit, state }, { queryParams }) {
      return api.getSubscribableCount(queryParams)
        .then(storeAllEntities(commit))
    },

    updateSubscribableAnswers ({ commit, state }, { subscribableId, data }) {
      return api.updateSubscribableAnswers(subscribableId, data)
        .then(storeAllEntities(commit))
        .then(resources => {
          for (const resource of resources.primaryResources) {
            commit('ADD_MY_ANSWER_TO_SUBSCRIBABLE', {
              subscribableId,
              studentAnswerId: resource.id,
            })
          }
        })
    },

    subscribeToQueue ({ commit, state }, { subscribableId, data }) {
      return api.subscribeToQueue(subscribableId, data)
        .then(storeAllEntities(commit))
        .then(resources => {
          commit('ADD_MY_QUEUE_SUBSCRIPTION_TO_SUBSCRIBABLE', {
            subscribableId,
            queueSubscriptionId: resources.primaryResources.map(s => s.id)[0],
          })
        })
    },

    unsubscribeFromQueue ({ commit, state }, { subscribableId }) {
      return api.unsubscribeFromQueue(subscribableId)
        .then(storeAllEntities(commit))
        .then(() => {
          commit('CLEAR_MY_QUEUE_SUBSCRIPTION', { subscribableId })
        })
    },
  },
}
